import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBoardList } from '../api/liveCommentService';
import { getStorage, STORAGE_KEYS } from '@shared/api/client';
import { ROUTES } from '@app/router/routes';
import LiveBadge from '@shared/components/LiveBadge';
import AuthModal from '@features/auth/components/AuthModal';
import { useLanguage } from '@shared/contexts/LanguageContext';

function getBoardStatus(start, end, settled) {
  if (settled) return 'settled';
  const now = Date.now();
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (now >= s && now <= e) return 'active';
  if (now < s) return 'upcoming';
  return 'ended';
}

function formatDateShort(dateStr) {
  return new Date(dateStr).toLocaleString('zh-TW', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function formatCountdown(startStr) {
  const diff = new Date(startStr).getTime() - Date.now();
  if (diff <= 0) return null;
  const totalSec = Math.floor(diff / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const hh = String(d * 24 + h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function useCountdown(startStr, isUpcoming) {
  const [text, setText] = useState(() => isUpcoming ? formatCountdown(startStr) : null);
  useEffect(() => {
    if (!isUpcoming) return;
    const timer = setInterval(() => {
      const t = formatCountdown(startStr);
      setText(t);
      if (!t) {
        clearInterval(timer);
        window.location.reload();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [startStr, isUpcoming]);
  return text;
}

function BoardCard({ board, status, onNavigate }) {
  const countdown = useCountdown(board.start_time, status === 'upcoming');
  const isUpcoming = status === 'upcoming';
  const isDisabled = isUpcoming;

  return (
    <button
      onClick={isDisabled ? undefined : onNavigate}
      className={`w-full text-left bg-white rounded-2xl transition-all duration-200 overflow-hidden ${isDisabled ? 'cursor-not-allowed opacity-80' : 'cursor-pointer group'}`}
      style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.10)' }}
    >
      <div className="flex items-center gap-4 px-5 py-5">
        <div className={`shrink-0 w-12 h-12 rounded-2xl bg-[--color-buddha-beige] flex items-center justify-center ${!isDisabled ? 'group-hover:bg-[--color-buddha-sand]' : ''} transition-colors duration-200`}>
          <svg width="32" height="28" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.5 1C8.38704 1 1 8.16285 1 16.9992C1 20.0999 1.91052 22.995 3.48521 25.4471C2.69163 27.2416 1.10758 30.8457 1.21516 30.761L7.50926 29.733C10.2829 31.7815 13.7441 33 17.5 33C26.613 33 34 25.8372 34 17.0008C34 8.16436 26.613 1 17.5 1Z" fill="white" stroke="black" strokeWidth="2" strokeMiterlimit="10"/>
            <path d="M25.5 7C32.179 7 37.5 12.3186 37.5 19C37.5 21.3256 36.8469 23.4942 35.6909 25.3186C36.2785 26.6472 37.4069 29.35 37.3282 29.2878L32.6261 28.5148C30.6075 30.0444 28.1589 30.9508 25.4992 30.9508C18.8202 30.9508 13.5 25.6322 13.5 19C13.5 12.3186 18.8202 7 25.4992 7H25.5Z" fill="white" stroke="black" strokeWidth="2" strokeMiterlimit="10"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-xl font-medium text-black transition-colors duration-200 ${!isDisabled ? 'group-hover:text-[--color-primary]' : ''}`}>
              {board.title}
            </p>
            {status === 'settled' && <LiveBadge type="settled" size="md" />}
            {status === 'active' && <LiveBadge type="live" size="md" />}
            {status === 'upcoming' && <LiveBadge type="upcoming" size="md" />}
            {status === 'ended' && <LiveBadge type="ended" size="md" />}
          </div>
          <p className="text-sm font-light text-black mt-1">
            {formatDateShort(board.start_time)} – {formatDateShort(board.end_time)}
          </p>
          {countdown && (
            <p className="text-lg font-bold tabular-nums tracking-widest text-blue-500 mt-1">{countdown}</p>
          )}
        </div>
        <span
          className={`material-symbols-outlined transition-colors duration-200 shrink-0 ${isDisabled ? 'text-[#C8BDB7]' : 'text-[--color-buddha-sand] group-hover:text-[--color-primary]'}`}
          style={{ fontSize: '20px' }}
        >
          chevron_right
        </span>
      </div>
    </button>
  );
}

export default function BoardListPage() {
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage.code === 'en';
  const [boards, setBoards] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [isMember, setIsMember] = useState(!!getStorage(STORAGE_KEYS.MEMBER_TOKEN));
  const [showAuth, setShowAuth] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isMember) return;
    getBoardList().then(res => {
      if (res.success) setBoards(res.data.items ?? []);
      setLoaded(true);
    });
  }, [isMember]);

  return (
    <div className="min-h-[calc(100dvh-120px)] bg-[#FAF7F3]">

      {/* Hero */}
      <div className="relative border-b border-[#E8DCC8] text-center -mt-[70px] md:-mt-[86px] overflow-hidden md:min-h-[calc(86px+160px)]">
        <div className="pt-[70px] md:pt-0">
          <img
            src="/image/live-comment/header_mobile.jpg"
            alt=""
            className="block md:hidden w-full"
          />
          <img
            src="/image/live-comment/header_desktop.jpg"
            alt=""
            className="hidden md:block absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center pt-[90px] md:pt-[106px] px-4 pb-4">
          <p className="text-xs tracking-[0.3em] text-[--color-accent] mb-1 uppercase">Live Comment</p>
          <h1 className={`${isEn ? 'text-xl' : 'text-2xl'} font-black text-[--color-text-main] text-center`}>
            {isEn ? <>Chan Heart<br />Connection</> : t('live-comment.heroTitle')}
          </h1>
          <p className="text-sm text-[--color-text-sub] mt-1 text-center">
            {isEn ? <>Select a message board<br />to enter a session</> : t('live-comment.heroSubtitle')}
          </p>
        </div>
      </div>

      {/* Board 列表 */}
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">

        {!isMember && (
          <div className="bg-white rounded-2xl p-6 text-center shadow-sm" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <span
              className="material-symbols-outlined text-[--color-buddha-sand] mb-3 block"
              style={{ fontVariationSettings: "'FILL' 1", fontSize: '48px' }}
            >
              person_add
            </span>
            <p className="text-base font-bold text-[--color-text-main] mb-2">{t('live-comment.notLoggedInTitle')}</p>
            <p className="text-sm text-[--color-text-sub] mb-4">{t('live-comment.notLoggedInDesc')}</p>
            <button
              onClick={() => setShowAuth(true)}
              className="inline-flex items-center gap-2 border border-[--color-primary] text-[--color-primary] bg-white text-sm font-medium px-6 py-2.5 rounded-full transition-all duration-200"
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-background-alt)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>login</span>
              {t('live-comment.loginRegister')}
            </button>
          </div>
        )}

        <AuthModal
          open={showAuth}
          onClose={() => setShowAuth(false)}
          onSuccess={() => {
            setIsMember(true);
            setShowAuth(false);
          }}
          defaultMode="login"
        />

        {isMember && loaded && boards.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
            <span style={{ fontFamily: 'Noto Serif TC', fontSize: 16, fontWeight: 400, color: 'rgba(188,49,61,0.5)', letterSpacing: '0.2em' }}>{t('live-comment.comingSoon')}</span>
          </div>
        )}

        {boards.map(board => {
          const status = getBoardStatus(board.start_time, board.end_time, board.settled);
          return (
            <BoardCard
              key={board.board_id}
              board={board}
              status={status}
              onNavigate={() => navigate(ROUTES.LIVE_COMMENT_BOARD(board.board_id), { state: { boardStatus: status } })}
            />
          );
        })}
      </div>
    </div>
  );
}
